import {
  fetchAdminProfile,
  uploadAdminProfilePhotoService,
  removeAdminProfilePhotoService,
  updateCompanyAdminProfile,
  changePasswordService,
} from "@/services/adminService";
import { toast } from "sonner";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Shield, Camera, Upload, Trash2, Loader2, EyeOff, Eye } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

const AdminProfile = () => {
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState({
    name: "",
    email: "",
    profilePhoto: "",
    two_factor_enabled: false,
  });

  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 
  const initials =
    profile?.name
      ?.split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "AD";

  // -------------------------------------------------
  // Load Profile
  // -------------------------------------------------
  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await fetchAdminProfile();
      const data = res?.data ?? res;

      setProfile({
        name: data?.name ?? "",
        email: data?.email ?? "",
        profilePhoto: data?.profilePhoto ?? "",
        two_factor_enabled: Boolean(data?.two_factor_enabled),
      });
    } catch {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // -------------------------------------------------
  // Profile Update
  // -------------------------------------------------
  const handleUpdateProfile = async () => {
    try {
      setSaving(true);

      await updateCompanyAdminProfile({
        name: profile.name,
        email: profile.email,
        two_factor_enabled: profile.two_factor_enabled,
      });

      toast.success("Profile updated successfully");
      await fetchProfile();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------
  // Photo Upload
  // -------------------------------------------------
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Maximum file size is 5MB");
      return;
    }

    try {
      setUploadingPhoto(true);

      const formData = new FormData();
      formData.append("profilePhoto", file);

      await uploadAdminProfilePhotoService(formData);
      toast.success("Photo updated");
      await fetchProfile();
    } catch (err) {
      toast.error("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePhoto = async () => {
    try {
      setUploadingPhoto(true);
      await removeAdminProfilePhotoService();
      toast.success("Photo removed");
      await fetchProfile();
    } catch {
      toast.error("Failed to remove photo");
    } finally {
      setUploadingPhoto(false);
      setConfirmOpen(false);
    }
  };

  // -------------------------------------------------
  // Password Change
  // -------------------------------------------------
  const changePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setChangingPassword(true);
      await changePasswordService(passwordData);
      toast.success("Password changed successfully");

      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  // -------------------------------------------------
  // UI
  // -------------------------------------------------
  return (
    <div className="space-y-6 ">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
          Admin Profile
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage your account information and security settings.
        </p>
      </div>

      {loading ? (
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-[220px_1fr] gap-8">
                <div className="space-y-3">
                  <div className="flex justify-center">
                    <Skeleton className="h-28 w-28 rounded-full" />
                  </div>
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    ))}
                  </div>
                  <Skeleton className="h-10 w-40" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <div className="flex justify-end">
                <Skeleton className="h-10 w-40" />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">
          {/* ================= PROFILE CARD ================= */}
          <Card>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-[220px_1fr] gap-8">
                {/* Avatar Section */}
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <Avatar className="h-28 w-28">
                      <AvatarImage src={profile.profilePhoto} />
                      <AvatarFallback className="text-xl">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoUpload}
                    accept="image/*"
                    className="hidden"
                  />

                  <div className="space-y-2">
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          {profile.profilePhoto ? "Change" : "Upload"}
                        </>
                      )}
                    </Button>

                    {profile.profilePhoto && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setConfirmOpen(true)}
                        disabled={uploadingPhoto}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground text-center">
                    JPG, PNG · Max 5MB
                  </p>
                </div>

                {/* Personal Info */}
                <div className="space-y-6">
                  {/* 2FA Row */}
                  <div className="flex items-center justify-between border rounded-md p-3">
                    <div>
                      <p className="text-sm font-medium">
                        Two-Factor Authentication
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Adds extra security to your account
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Badge
                        variant={
                          profile.two_factor_enabled ? "default" : "secondary"
                        }
                      >
                        {profile.two_factor_enabled ? "Enabled" : "Disabled"}
                      </Badge>
                      <Switch
                        checked={profile.two_factor_enabled}
                        onCheckedChange={(checked) =>
                          setProfile((prev) => ({
                            ...prev,
                            two_factor_enabled: checked,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Name & Email */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label>Name</Label>
                      <Input
                        value={profile.name}
                        onChange={(e) =>
                          setProfile((p) => ({
                            ...p,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="space-y-1">
                      <Label>Email</Label>
                      <Input value={profile.email} disabled />
                      <p className="text-xs text-muted-foreground">
                        Email cannot be modified.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleUpdateProfile} disabled={saving}>
                      {saving ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ================= SECURITY CARD ================= */}
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center gap-2 font-semibold">
                <Shield className="h-4 w-4" />
                Change Password
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <Input
                  type="password"
                  placeholder="Current password"
                  value={passwordData.current_password}
                  onChange={(e) =>
                    setPasswordData((p) => ({
                      ...p,
                      current_password: e.target.value,
                    }))
                  }
                />

                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="New password"
                    value={passwordData.new_password}
                    onChange={(e) =>
                      setPasswordData((p) => ({
                        ...p,

                        new_password: e.target.value,
                      }))
                    }
                    className="pr-10"
                  />

                  {passwordData.new_password.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>

                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm password"
                    value={passwordData.confirm_password}
                    onChange={(e) =>
                      setPasswordData((p) => ({
                        ...p,

                        confirm_password: e.target.value,
                      }))
                    }
                    className="pr-10"
                  />

                  {passwordData.confirm_password.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={changePassword}
                  disabled={
                    changingPassword ||
                    !passwordData.current_password ||
                    !passwordData.new_password ||
                    !passwordData.confirm_password
                  }
                >
                  {changingPassword ? "Changing..." : "Update Password"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Remove Profile Photo"
        message="Are you sure you want to remove your profile photo?"
        onConfirm={handleRemovePhoto}
        destructive
      />
    </div>
  );
};

export default AdminProfile;
